import { Flex, Icon, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { TbCheck } from 'react-icons/tb';
import { PARTBAY_BLUE, PARTBAY_BLUE_TINT } from './constants';

/** The filled tick that marks a chosen option, pinned to the row's right edge. */
export const SettingsOptionCheck = () => (
    <Flex
        boxSize="18px"
        borderRadius="full"
        bg={PARTBAY_BLUE}
        align="center"
        justify="center"
        flexShrink={0}
    >
        <Icon as={TbCheck as React.ElementType} boxSize={3} color="white" />
    </Flex>
);

export interface SettingsOptionRowProps {
    /** A string is rendered in the row's own type treatment; nodes as given. */
    label: ReactNode;
    isSelected: boolean;
    onToggle: () => void;
    isDisabled?: boolean;
    /** Reads as a heading over the options below it (an "All …" master row). */
    isEmphasized?: boolean;
    /** Indents an option that refines the one above it. */
    isNested?: boolean;
    /** Right-hand content for an unselected row (a count, a hint). */
    trailing?: ReactNode;
    /**
     * Let a long label wrap onto more lines instead of being truncated. For
     * option sets written as sentences rather than names.
     */
    allowWrap?: boolean;
    ariaLabel?: string;
}

/**
 * One option inside a cell's edit dropdown. What is currently chosen reads as
 * chosen without hunting for a tick box: the row itself is tinted brand blue and
 * carries a filled check at its right edge. Rows are the full width of the panel
 * so the whole thing is the target.
 *
 * Shared by every editable-cell dropdown in Settings — single-selects and
 * multi-selects alike — so picking a role, a position, an OEM or a carrier all
 * look and behave the same.
 */
export const SettingsOptionRow = ({
    label,
    isSelected,
    onToggle,
    isDisabled = false,
    isEmphasized = false,
    isNested = false,
    trailing,
    allowWrap = false,
    ariaLabel,
}: SettingsOptionRowProps) => (
    <Flex
        as="button"
        type="button"
        disabled={isDisabled}
        aria-pressed={isSelected}
        aria-label={ariaLabel}
        onClick={() => !isDisabled && onToggle()}
        w="100%"
        align={allowWrap ? 'flex-start' : 'center'}
        justify="space-between"
        gap={3}
        textAlign="left"
        px={3}
        pl={isNested ? 7 : 3}
        py={2}
        borderRadius="md"
        bg={isSelected ? PARTBAY_BLUE_TINT : 'transparent'}
        opacity={isDisabled ? 0.5 : 1}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        transition="background 0.12s ease"
        _hover={isDisabled ? undefined : { bg: PARTBAY_BLUE_TINT }}
    >
        {typeof label === 'string' ? (
            <Text
                fontSize="sm"
                fontWeight={isSelected || isEmphasized ? 'semibold' : 'normal'}
                color={isSelected ? PARTBAY_BLUE : isEmphasized ? 'gray.800' : 'gray.700'}
                noOfLines={allowWrap ? undefined : 1}
            >
                {label}
            </Text>
        ) : (
            label
        )}
        {isSelected ? <SettingsOptionCheck /> : trailing}
    </Flex>
);

export default SettingsOptionRow;
